import { SDK, docs } from './sdk';

export interface PlatformLayer {
  label: string;
  summary: string;
  items: string[];
}

export interface PlatformCapability {
  title: string;
  outcome: string;
  detail: string;
}

export interface WorkflowExample {
  title: string;
  summary: string;
  actions: string[];
}

export interface SdkPackage {
  name: string;
  install: string;
  detail: string;
}

export const PLATFORM_LAYERS: PlatformLayer[] = [
  {
    label: 'Workspace',
    summary: 'Define agents, roles, schedules, and the work they are allowed to do.',
    items: ['Agent definitions', 'Tasks and queues', 'Policies and tool scopes'],
  },
  {
    label: 'Managed runtime',
    summary: 'Run each task in a separated environment with the context it needs.',
    items: ['Isolated execution', 'Memory and artifacts', 'Live task state'],
  },
  {
    label: 'Governed action',
    summary: 'Check every read, draft, and execute request before it reaches a system.',
    items: ['Policy evaluation', 'Scoped credentials', 'Human approval'],
  },
  {
    label: 'Evidence',
    summary: 'Keep the record of intent, sources, decisions, and outcomes together.',
    items: ['Traces and audit logs', 'Replayable decisions', 'Verifiable artifacts'],
  },
];

export const PLATFORM_CAPABILITIES: PlatformCapability[] = [
  {
    title: 'Agent control plane',
    outcome: 'Define how work should happen.',
    detail: 'Set roles, instructions, schedules, policies, and allowed tools from one operating layer.',
  },
  {
    title: 'Isolated execution',
    outcome: 'Give every task a safe place to run.',
    detail: 'Run workloads in separated managed runtimes with the data, memory, and artifacts that task needs.',
  },
  {
    title: 'Secure connections',
    outcome: 'Connect agents to the systems they need.',
    detail: 'Broker scoped access to business systems without handing long-lived credentials to the agent.',
  },
  {
    title: 'Governed actions',
    outcome: 'Make permission part of execution.',
    detail: 'Classify operations as read, draft, or execute and evaluate policy before an action runs.',
  },
  {
    title: 'Human authority',
    outcome: 'Keep sensitive decisions reviewable.',
    detail: 'Route high-impact actions to an approval queue while routine work continues automatically.',
  },
  {
    title: 'Operational evidence',
    outcome: 'Make every outcome explainable.',
    detail: 'Record sources, calculations, tool calls, results, and policy decisions for replay and review.',
  },
];

export const WORKFLOWS: WorkflowExample[] = [
  {
    title: 'Financial operations',
    summary: 'Move from analysis to controlled execution across finance systems.',
    actions: ['Reconcile data', 'Prepare forecasts', 'Draft journal entries', 'Escalate execution'],
  },
  {
    title: 'Compliance and review',
    summary: 'Turn scattered evidence into a review package people can defend.',
    actions: ['Collect evidence', 'Apply policy', 'Prepare findings', 'Preserve approvals'],
  },
  {
    title: 'Customer operations',
    summary: 'Let agents resolve cases while customer-impacting actions stay governed.',
    actions: ['Research a case', 'Draft a response', 'Update systems', 'Control escalation'],
  },
  {
    title: 'Research and reporting',
    summary: 'Combine approved sources, calculations, and artifacts into traceable work.',
    actions: ['Gather sources', 'Run calculations', 'Create artifacts', 'Cite the record'],
  },
];

export const SDK_PACKAGES: SdkPackage[] = [
  {
    name: 'Python SDK',
    install: SDK.install,
    detail: `Apache-2.0 · Python ${SDK.python.replace('Python ', '')} · Rust-backed core`,
  },
  {
    name: 'TypeScript runtime',
    install: 'npm install @briefcase-ai/controls @briefcase-ai/runtime',
    detail: 'Node 20.19+ · Controls, traces, connectors, and integrity primitives',
  },
];

export const DEVELOPER_LINKS = [
  { label: 'Quickstart', href: docs('/getting-started/quickstart/') },
  { label: 'Govern agent actions', href: docs('/guides/govern-agent-actions/') },
  { label: 'Decision recording', href: docs('/features/decision-recording/') },
  { label: 'Replay', href: docs('/features/replay/') },
  { label: 'Audit bundles', href: docs('/features/compliance-bundles/') },
];
